import React, { useMemo } from "react";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider } from "react-redux";
import { FirebaseProvider, store } from "common";
import { FirebaseConfig } from './config/FirebaseConfig';
import { GoogleMapApiConfig } from './config/GoogleMapApiConfig';
import { GoogleMapApiOptions } from './mapConfig';
import { webClientId } from "config/ClientIds.js";
import { ThemeProvider } from '@mui/styles';
import { createTheme } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from '@react-oauth/google';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Theme and Context
import { ThemeModeProvider, useThemeMode } from "components/Theme/ThemeModeContext.js";

// App Views
import AuthLoading from './views/AuthLoading';
import ProtectedRoute from './views/ProtectedRoute';
import LandingPage from "./views/LandingPage";
import LoginPage from "./views/LoginPage";
import PrivacyPolicy from "./views/PrivacyPolicy";
import AboutUs from "./views/AboutUs";
import ContactUs from "./views/ContactUs";
import TermCondition from "views/TermCondition";
import MyProfile from './views/MyProfile';
import BookingHistory from './views/BookingHistory';
import BookingDetails from "views/BookingDetails";
import Dashboard from './views/Dashboard';
import CarTypes from './views/CarTypes';
import EditCarType from "views/EditCarType";
import CarsList from "./views/CarsList";
import EditCar from "views/EditCar";
import AddBookings from './views/AddBookings';
import Promos from './views/Promos';
import EditPromo from "views/EditPromo";
import Users from './views/Users';
import EditUser from "views/EditUser";
import CustomerDetails from "views/CustomerDetails";
import DriverDetails from "views/DriverDetails";
import UserDocuments from "views/UserDocuments";
import Notifications from './views/Notifications';
import AddNotifications from "views/AddNotifications";
import Settings from './views/Settings';
import PaymentSettings from "views/PaymentSettings";
import Complain from "views/Complain";
import Sos from './views/Sos';
import AddMoney from "./views/AddMoney";
import UserWallet from "./views/UserWallet";
import Withdraws from './views/Withdraws';
import AllReports from "./views/AllReports";
import AdminDashboard from "views/AdminDashboard";
import AdminCreation from "views/AdminCreation";
import Error from "views/Error";

// i18n Init
i18n.use(initReactI18next).init({
  resources: {},
  fallbackLng: "en",
  ns: ["translations"],
  defaultNS: "translations",
  interpolation: {
    escapeValue: false
  }
});

const libraries = GoogleMapApiOptions.libraries;

function ThemedApp() {
  const { themeMode } = useThemeMode();

  const theme = useMemo(() => {
    let mode = themeMode;
    if (mode === 'system') {
      mode = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return createTheme({
      palette: {
        mode,
        background: mode === 'dark'
          ? { default: '#181A20', paper: '#23272F' }
          : { default: '#F3F4F6', paper: '#fff' },
        primary: { main: '#1E3A8A' },
        secondary: { main: '#FFD700' }
      },
      typography: {
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
  }, [themeMode]);

  useJsApiLoader({
    id: 'google-map',
    googleMapsApiKey: GoogleMapApiConfig,
    version: GoogleMapApiOptions.v,
    libraries,
  });

  return (
    <HelmetProvider>
      <Provider store={store}>
        <FirebaseProvider config={FirebaseConfig}>
          <GoogleOAuthProvider clientId={webClientId}>
            <ThemeProvider theme={theme}>
              <AuthLoading>
                <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/dashboard" element={<ProtectedRoute permit="admin,fleetadmin"><Dashboard /></ProtectedRoute>} />
                    <Route path="/bookings" element={<ProtectedRoute permit="customer,admin,driver,fleetadmin,admin_munincipality"><BookingHistory /></ProtectedRoute>} />
                    <Route path="/bookings/bookingdetails/:id" element={<ProtectedRoute permit="customer,admin,driver,fleetadmin,admin_munincipality"><BookingDetails /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute permit="customer,admin,driver,fleetadmin"><MyProfile /></ProtectedRoute>} />
                    <Route path="/cartypes" element={<ProtectedRoute permit="admin"><CarTypes /></ProtectedRoute>} />
                    <Route path="/cartypes/editcartype" element={<ProtectedRoute permit="admin,fleetadmin,driver"><EditCarType /></ProtectedRoute>} />
                    <Route path="/cartypes/editcartype/:id" element={<ProtectedRoute permit="admin,fleetadmin,driver"><EditCarType /></ProtectedRoute>} />
                    <Route path="/cars" element={<ProtectedRoute permit="admin,fleetadmin,driver"><CarsList /></ProtectedRoute>} />
                    <Route path="/cars/editcar" element={<ProtectedRoute permit="admin,fleetadmin,driver"><EditCar /></ProtectedRoute>} />
                    <Route path="/cars/editcar/:id" element={<ProtectedRoute permit="admin,fleetadmin,driver"><EditCar /></ProtectedRoute>} />
                    <Route path="/addbookings" element={<ProtectedRoute permit="admin,fleetadmin,customer"><AddBookings /></ProtectedRoute>} />
                    <Route path="/promos" element={<ProtectedRoute permit="admin"><Promos /></ProtectedRoute>} />
                    <Route path="/promos/editpromo" element={<ProtectedRoute permit="admin"><EditPromo /></ProtectedRoute>} />
                    <Route path="/promos/editpromo/:id" element={<ProtectedRoute permit="admin"><EditPromo /></ProtectedRoute>} />
                    <Route path="/users/:id" element={<ProtectedRoute permit="admin,fleetadmin"><Users /></ProtectedRoute>} />
                    <Route path="/users/edituser/:usertype" element={<ProtectedRoute permit="admin,fleetadmin,admin_national,admin_regional,admin_provincial,admin_munincipality"><EditUser /></ProtectedRoute>} />
                    <Route path="/users/edituser/:usertype/:id" element={<ProtectedRoute permit="admin,fleetadmin,admin_national,admin_regional,admin_provincial,admin_munincipality"><EditUser /></ProtectedRoute>} />
                    <Route path="/users/customerdetails/:id" element={<ProtectedRoute permit="admin,fleetadmin"><CustomerDetails /></ProtectedRoute>} />
                    <Route path="/users/driverdetails/:id" element={<ProtectedRoute permit="admin,fleetadmin"><DriverDetails /></ProtectedRoute>} />
                    <Route path="/users/userdocuments/:rId/:id" element={<ProtectedRoute permit="admin,fleetadmin"><UserDocuments /></ProtectedRoute>} />
                    <Route path="/notifications" element={<ProtectedRoute permit="admin"><Notifications /></ProtectedRoute>} />
                    <Route path="/notifications/addnotifications" element={<ProtectedRoute permit="admin"><AddNotifications /></ProtectedRoute>} />
                    <Route path="/addtowallet" element={<ProtectedRoute permit="admin"><AddMoney /></ProtectedRoute>} />
                    <Route path="/userwallet" element={<ProtectedRoute permit="customer,driver,fleetadmin"><UserWallet /></ProtectedRoute>} />
                    <Route path="/withdraws" element={<ProtectedRoute permit="admin"><Withdraws /></ProtectedRoute>} />
                    <Route path="/sos" element={<ProtectedRoute permit="admin"><Sos /></ProtectedRoute>} />
                    <Route path="/complain" element={<ProtectedRoute permit="admin"><Complain /></ProtectedRoute>} />
                    <Route path="/allreports" element={<ProtectedRoute permit="admin,fleetadmin"><AllReports /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute permit="admin"><Settings /></ProtectedRoute>} />
                    <Route path="/paymentsettings" element={<ProtectedRoute permit="admin"><PaymentSettings /></ProtectedRoute>} />
                    <Route path="/admin_dashboard" element={<ProtectedRoute permit="admin_national,admin_regional,admin_provincial,admin_munincipality"><AdminDashboard /></ProtectedRoute>} />
                    <Route path="/admin_creation/:id" element={<ProtectedRoute permit="admin_national,admin_regional,admin_provincial,admin_munincipality"><AdminCreation /></ProtectedRoute>} />
                    <Route path="/contact-us" element={<ContactUs />} />
                    <Route path="/about-us" element={<AboutUs />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/term-condition" element={<TermCondition />} />
                    <Route path="/*" element={<Error />} />
                  </Routes>
                </BrowserRouter>
              </AuthLoading>
            </ThemeProvider>
          </GoogleOAuthProvider>
        </FirebaseProvider>
      </Provider>
    </HelmetProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  );
}

export default App;